/**
 * PortalPageEditor — Visual page builder for portal pages
 *
 * Allows admins to compose dynamic pages with widgets using drag-and-drop.
 *
 * Features:
 *   - Widget palette: browse all registry widgets + text widget type
 *   - Drag-and-drop grid layout (react-grid-layout)
 *   - Per-widget configuration: type, registryId, text content, CASL, userEditable
 *   - Text widget translatable content (multi-language)
 *   - Widget preview with live data
 *   - Page metadata (name, slug, description, isActive)
 *   - Save page layout + widget configs to portal via API
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Responsive, WidthProvider } from "react-grid-layout";
import {
  Box,
  Card,
  Drawer,
  Tabs,
  Tab,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  Autocomplete,
  IconButton,
  Tooltip,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItemIcon,
  ListItemText,
  ListItemButton,
} from "@mui/material";
import {
  Save as SaveIcon,
  ArrowBack as BackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Settings as ConfigIcon,
  DragIndicator as DragIcon,
  Widgets as PaletteIcon,
  TextFields as TextIcon,
  BarChart as ChartIcon,
  TableChart as TableIcon,
  Speed as GaugeIcon,
  TrendingUp as StatIcon,
  Close as CloseIcon,
  Visibility as PreviewIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
} from "@mui/icons-material";
import MuiIcon from "@mui/material/Icon";
import { useTheme } from "@mui/material/styles";
import KDBox from "components/KDBox";
import KDTypography from "components/KDTypography";
import KDButton from "components/KDButton";
import KDSnackbar from "components/KDSnackbar";
import WidgetRenderer from "layouts/dashboard/components/WidgetRenderer";
import { getAllWidgets, DEFAULT_SIZES, WIDGET_TYPES } from "layouts/dashboard/config/widgetRegistry";
import { useMaterialUIController } from "context";
import { useT } from "context/I18nContext";
import usePortals from "hooks/usePortals";
import logger from "services/logger";

import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

const ResponsiveGridLayout = WidthProvider(Responsive);

// Available languages for text widget translations
const LANGUAGES = [
  { code: "en", label: "English" },
  { code: "fr", label: "Français" },
  { code: "es", label: "Español" },
  { code: "de", label: "Deutsch" },
  { code: "it", label: "Italiano" },
  { code: "pt", label: "Português" },
  { code: "zh", label: "中文" },
  { code: "ja", label: "日本語" },
];

// Widget type icons for the palette
const TYPE_ICONS = {
  [WIDGET_TYPES.STAT_CARD]: <StatIcon fontSize="small" />,
  [WIDGET_TYPES.LINE_CHART]: <ChartIcon fontSize="small" />,
  [WIDGET_TYPES.BAR_CHART]: <ChartIcon fontSize="small" />,
  [WIDGET_TYPES.PIE_CHART]: <ChartIcon fontSize="small" />,
  [WIDGET_TYPES.AREA_CHART]: <ChartIcon fontSize="small" />,
  [WIDGET_TYPES.TABLE]: <TableIcon fontSize="small" />,
  [WIDGET_TYPES.LEADERBOARD]: <TableIcon fontSize="small" />,
  [WIDGET_TYPES.GAUGE]: <GaugeIcon fontSize="small" />,
  [WIDGET_TYPES.SPARKLINE]: <StatIcon fontSize="small" />,
  [WIDGET_TYPES.KPI]: <StatIcon fontSize="small" />,
  [WIDGET_TYPES.HEATMAP]: <ChartIcon fontSize="small" />,
  [WIDGET_TYPES.DONUT]: <ChartIcon fontSize="small" />,
  [WIDGET_TYPES.TEXT]: <TextIcon fontSize="small" />,
};

// ==================== Widget Config Dialog ====================
function WidgetConfigDialog({
  open,
  onClose,
  widgetKey,
  widgetConfig,
  onSave,
  portalLanguages,
  darkMode,
}) {
  const [config, setConfig] = useState({});
  const [transLang, setTransLang] = useState("en");

  useEffect(() => {
    if (widgetConfig) {
      setConfig({ ...widgetConfig });
    }
  }, [widgetConfig, open]);

  const { palette } = useTheme();
  const textPrimary = palette.text.main;
  const textSecondary = palette.text.secondary;
  const borderColor = palette.divider;
  const dialogBg = palette.background.paper;
  const inputSx = {
    "& .MuiOutlinedInput-root": {
      color: palette.text.main,
      "& fieldset": { borderColor: palette.inputBorderColor },
      "&:hover fieldset": { borderColor: palette.inputBorderHoverColor },
    },
    "& .MuiInputLabel-root": { color: palette.text.secondary },
  };

  const isText = config.type === "text";

  const updateConfig = (field, value) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
  };

  const updateNestedConfig = (field, value) => {
    setConfig((prev) => ({
      ...prev,
      config: { ...(prev.config || {}), [field]: value },
    }));
  };

  const updateTranslation = (lang, value) => {
    const currentContent = config.config?.content || {};
    const newContent = typeof currentContent === "string"
      ? { en: currentContent, [lang]: value }
      : { ...currentContent, [lang]: value };
    updateNestedConfig("content", newContent);
  };

  const handleSave = () => {
    onSave(widgetKey, config);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { bgcolor: dialogBg, color: textPrimary } }}
    >
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <KDTypography variant="h6" sx={{ color: textPrimary }}>
          Widget Configuration
        </KDTypography>
        <IconButton onClick={onClose} sx={{ color: textSecondary }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ borderColor }}>
        <Box display="flex" flexDirection="column" gap={2.5} pt={1}>
          {/* Widget type indicator */}
          <Box display="flex" alignItems="center" gap={1}>
            {TYPE_ICONS[config.type] || <PaletteIcon fontSize="small" />}
            <KDTypography variant="body2" sx={{ color: textSecondary }}>
              Type: <strong style={{ color: textPrimary }}>{config.type || "N/A"}</strong>
              {config.registryId && (
                <span> — Registry: <strong>{config.registryId}</strong></span>
              )}
            </KDTypography>
          </Box>

          {/* Text widget: translatable content */}
          {isText && (
            <>
              <Divider sx={{ borderColor }}>
                <Chip label="Content" size="small" sx={{ color: textSecondary }} />
              </Divider>

              <Autocomplete
                options={WIDGET_TYPES.TEXT ? ["body1", "body2", "h1", "h2", "h3", "h4", "h5", "h6", "subtitle1", "subtitle2", "caption"] : []}
                value={config.config?.variant || "body1"}
                onChange={(_, v) => updateNestedConfig("variant", v || "body1")}
                renderInput={(params) => (
                  <TextField {...params} label="Typography Variant" size="small" sx={inputSx} />
                )}
                disableClearable
              />

              <Autocomplete
                options={["left", "center", "right", "justify"]}
                value={config.config?.align || "left"}
                onChange={(_, v) => updateNestedConfig("align", v || "left")}
                renderInput={(params) => (
                  <TextField {...params} label="Text Alignment" size="small" sx={inputSx} />
                )}
                disableClearable
              />

              {/* Multi-language text content */}
              <KDTypography variant="body2" sx={{ color: textSecondary, fontWeight: 600 }}>
                Text Content (per language)
              </KDTypography>

              <Box display="flex" gap={1} mb={1}>
                {(portalLanguages || ["en"]).map((lang) => (
                  <Chip
                    key={lang}
                    label={LANGUAGES.find((l) => l.code === lang)?.label || lang}
                    size="small"
                    onClick={() => setTransLang(lang)}
                    variant={transLang === lang ? "filled" : "outlined"}
                    sx={{
                      color: transLang === lang ? "white" : textSecondary,
                      bgcolor: transLang === lang ? palette.info.main : "transparent",
                      borderColor,
                      cursor: "pointer",
                    }}
                  />
                ))}
              </Box>

              <TextField
                label={`Content (${LANGUAGES.find((l) => l.code === transLang)?.label || transLang})`}
                value={
                  typeof config.config?.content === "object"
                    ? config.config?.content[transLang] || ""
                    : transLang === "en"
                      ? config.config?.content || ""
                      : ""
                }
                onChange={(e) => updateTranslation(transLang, e.target.value)}
                fullWidth
                multiline
                rows={4}
                helperText="Supports **bold**, *italic*, and [link text](url) syntax"
                sx={inputSx}
              />

              <TextField
                label="Text Color (optional)"
                value={config.config?.color || ""}
                onChange={(e) => updateNestedConfig("color", e.target.value)}
                fullWidth
                size="small"
                helperText="CSS color value or leave empty for theme default"
                sx={inputSx}
              />
            </>
          )}

          <Divider sx={{ borderColor }}>
            <Chip label="Access Control" size="small" sx={{ color: textSecondary }} />
          </Divider>

          {/* CASL permission gating */}
          <Box display="flex" gap={2}>
            <TextField
              label="CASL Action"
              value={config.caslAction || ""}
              onChange={(e) => updateConfig("caslAction", e.target.value)}
              size="small"
              helperText="e.g. read"
              sx={{ ...inputSx, flex: 1 }}
            />
            <TextField
              label="CASL Subject"
              value={config.caslSubject || ""}
              onChange={(e) => updateConfig("caslSubject", e.target.value)}
              size="small"
              helperText="e.g. MetricUsers"
              sx={{ ...inputSx, flex: 1 }}
            />
          </Box>

          {/* User editable toggle */}
          <FormControlLabel
            control={
              <Switch
                checked={config.userEditable === true}
                onChange={(e) => updateConfig("userEditable", e.target.checked)}
                color="warning"
              />
            }
            label={
              <Box display="flex" alignItems="center" gap={1}>
                {config.userEditable ? (
                  <LockOpenIcon fontSize="small" sx={{ color: palette.warning.main }} />
                ) : (
                  <LockIcon fontSize="small" sx={{ color: textSecondary }} />
                )}
                <KDTypography variant="body2" sx={{ color: textPrimary }}>
                  User can edit this widget
                </KDTypography>
              </Box>
            }
          />
          <KDTypography variant="caption" sx={{ color: textSecondary, mt: -1.5 }}>
            Default is locked (false). When enabled, end-users can modify this widget's content.
          </KDTypography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${borderColor}` }}>
        <KDButton variant="text" color="secondary" onClick={onClose}>
          Cancel
        </KDButton>
        <KDButton variant="gradient" color="info" onClick={handleSave}>
          Apply
        </KDButton>
      </DialogActions>
    </Dialog>
  );
}

// ==================== Widget Palette Drawer ====================
function WidgetPalette({ open, onClose, onAddWidget, darkMode }) {
  const [paletteTab, setPaletteTab] = useState(0);
  const [search, setSearch] = useState("");
  const registryWidgets = useMemo(() => getAllWidgets(), []);

  const { palette } = useTheme();
  const textPrimary = palette.text.main;
  const textSecondary = palette.text.secondary;
  const borderColor = palette.divider;
  const drawerBg = palette.background.default;

  // Group registry widgets by category
  const categories = useMemo(() => {
    const cats = {};
    registryWidgets.forEach((w) => {
      const cat = w.category || "other";
      if (!cats[cat]) cats[cat] = [];
      cats[cat].push(w);
    });
    return cats;
  }, [registryWidgets]);

  const filteredCategories = useMemo(() => {
    if (!search) return categories;
    const filtered = {};
    Object.entries(categories).forEach(([cat, widgets]) => {
      const matches = widgets.filter(
        (w) =>
          w.title.toLowerCase().includes(search.toLowerCase()) ||
          w.id.toLowerCase().includes(search.toLowerCase())
      );
      if (matches.length > 0) filtered[cat] = matches;
    });
    return filtered;
  }, [categories, search]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 360,
          bgcolor: drawerBg,
          color: textPrimary,
          borderLeft: `1px solid ${borderColor}`,
        },
      }}
    >
      <Box p={2.5}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <KDTypography variant="h6" sx={{ color: textPrimary }}>
            Widget Palette
          </KDTypography>
          <IconButton onClick={onClose} sx={{ color: textSecondary }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <TextField
          fullWidth
          size="small"
          placeholder="Search widgets..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{
            mb: 2,
            "& .MuiOutlinedInput-root": {
              color: textPrimary,
              "& fieldset": { borderColor },
            },
          }}
        />

        <Tabs
          value={paletteTab}
          onChange={(_, v) => setPaletteTab(v)}
          sx={{
            mb: 2,
            minHeight: 36,
            "& .MuiTab-root": { color: textSecondary, textTransform: "none", minHeight: 36, py: 0.5 },
            "& .Mui-selected": { color: textPrimary },
          }}
        >
          <Tab label="Data Widgets" />
          <Tab label="Text / Content" />
        </Tabs>

        {/* Data widgets tab */}
        {paletteTab === 0 && (
          <Box>
            {Object.entries(filteredCategories).map(([category, widgets]) => (
              <Box key={category} mb={2}>
                <KDTypography
                  variant="caption"
                  sx={{ color: textSecondary, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1 }}
                >
                  {category}
                </KDTypography>
                <List dense sx={{ mt: 0.5 }}>
                  {widgets.map((widget) => (
                    <ListItemButton
                      key={widget.id}
                      onClick={() => {
                        onAddWidget("registry", widget);
                        onClose();
                      }}
                      sx={{
                        borderRadius: "8px",
                        mb: 0.5,
                        "&:hover": {
                          bgcolor: palette.background.cardInner,
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <MuiIcon sx={{ fontSize: 18, color: widget.color || textSecondary }}>
                          {widget.icon || "widgets"}
                        </MuiIcon>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <KDTypography variant="body2" sx={{ color: textPrimary, fontWeight: 500 }}>
                            {widget.title}
                          </KDTypography>
                        }
                        secondary={
                          <KDTypography variant="caption" sx={{ color: textSecondary }}>
                            {widget.type} · {widget.description || widget.id}
                          </KDTypography>
                        }
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Box>
            ))}
          </Box>
        )}

        {/* Text / Content widgets tab */}
        {paletteTab === 1 && (
          <Box>
            <KDTypography variant="body2" sx={{ color: textSecondary, mb: 2 }}>
              Text widgets display static or translatable content. Content can be set per language.
            </KDTypography>

            {[
              { variant: "h3", label: "Heading (H3)", desc: "Large title text" },
              { variant: "h5", label: "Heading (H5)", desc: "Medium title text" },
              { variant: "body1", label: "Body Text", desc: "Standard paragraph text" },
              { variant: "body2", label: "Small Text", desc: "Secondary paragraph text" },
              { variant: "caption", label: "Caption", desc: "Small caption or label" },
              { variant: "subtitle1", label: "Subtitle", desc: "Subtitle/description text" },
            ].map((textType) => (
              <ListItemButton
                key={textType.variant}
                onClick={() => {
                  onAddWidget("text", {
                    variant: textType.variant,
                    label: textType.label,
                  });
                  onClose();
                }}
                sx={{
                  borderRadius: "8px",
                  mb: 0.5,
                  "&:hover": {
                    bgcolor: palette.background.cardInner,
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <TextIcon sx={{ fontSize: 18, color: palette.secondary.main }} />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <KDTypography variant="body2" sx={{ color: textPrimary, fontWeight: 500 }}>
                      {textType.label}
                    </KDTypography>
                  }
                  secondary={
                    <KDTypography variant="caption" sx={{ color: textSecondary }}>
                      {textType.desc}
                    </KDTypography>
                  }
                />
              </ListItemButton>
            ))}
          </Box>
        )}
      </Box>
    </Drawer>
  );
}

// ==================== Main Component ====================
function PortalPageEditor() {
  const { id: portalId, pageId } = useParams();
  const isNew = pageId === "new";
  const navigate = useNavigate();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const t = useT();

  // Local snackbar state — rendered via KDSnackbar
  const [snackbar, setSnackbar] = useState({ open: false, severity: "info", title: "", message: "" });
  const showSnackbar = useCallback(
    (severity, title, message) => setSnackbar({ open: true, severity, title, message }),
    []
  );

  const { getPortal, upsertPage } = usePortals();

  const [portal, setPortal] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [configDialog, setConfigDialog] = useState({ open: false, widgetKey: null });
  const [previewMode, setPreviewMode] = useState(false);

  // Page form state
  const [pageMeta, setPageMeta] = useState({
    name: "",
    slug: "",
    description: "",
    isActive: true,
  });

  // Widget layout (react-grid-layout items)
  const [layout, setLayout] = useState([]);

  // Widget configs (keyed by widget instance ID)
  const [widgetConfigs, setWidgetConfigs] = useState({});

  // Counter for generating unique widget IDs
  const widgetCounter = useRef(1);

  // Load portal and page data
  useEffect(() => {
    (async () => {
      try {
        const p = await getPortal(portalId);
        if (!p) {
          showSnackbar("error", "Error", "Portal not found");
          navigate(`/admin/portals/${portalId}`);
          return;
        }
        setPortal(p);

        if (!isNew) {
          const page = (p.pages || []).find(
            (pg) => pg.id === pageId || pg.slug === pageId
          );
          if (page) {
            setPageMeta({
              name: page.name || "",
              slug: page.slug || "",
              description: page.description || "",
              isActive: page.isActive !== false,
            });
            setLayout(page.layout || []);
            setWidgetConfigs(page.widgets || {});
            // Set counter beyond existing widget IDs
            const maxNum = Object.keys(page.widgets || {}).reduce((max, key) => {
              const match = key.match(/widget-(\d+)/);
              return match ? Math.max(max, parseInt(match[1], 10)) : max;
            }, 0);
            widgetCounter.current = maxNum + 1;
          } else {
            showSnackbar("error", "Error", "Page not found in this portal");
            navigate(`/admin/portals/${portalId}`);
            return;
          }
        }
      } catch (err) {
        logger.error("[PortalPageEditor]", "Load error:", err.message);
        showSnackbar("error", "Error", "Failed to load portal");
      } finally {
        setInitialLoading(false);
      }
    })();
  }, [portalId, pageId, isNew, getPortal, navigate, showSnackbar]);

  // Style vars — derived from theme palette
  const { palette } = useTheme();
  const textPrimary = palette.text.main;
  const textSecondary = palette.text.secondary;
  const cardBg = palette.background.glass;
  const borderColor = palette.divider;
  const inputSx = {
    "& .MuiOutlinedInput-root": {
      color: palette.text.main,
      "& fieldset": { borderColor: palette.inputBorderColor },
      "&:hover fieldset": { borderColor: palette.inputBorderHoverColor },
    },
    "& .MuiInputLabel-root": { color: palette.text.secondary },
  };

  // Add widget from palette
  const handleAddWidget = useCallback(
    (type, widgetData) => {
      const widgetId = `widget-${widgetCounter.current++}`;

      let widgetConfig;
      let size;

      if (type === "registry") {
        // Data widget from registry
        widgetConfig = {
          type: "registry",
          registryId: widgetData.id,
          caslAction: "",
          caslSubject: "",
          userEditable: false,
        };
        size = DEFAULT_SIZES[widgetData.type] || { w: 4, h: 3, minW: 2, minH: 2 };
      } else {
        // Text widget
        widgetConfig = {
          type: "text",
          config: {
            content: { en: "" },
            variant: widgetData.variant || "body1",
            align: "left",
          },
          caslAction: "",
          caslSubject: "",
          userEditable: false,
        };
        size = DEFAULT_SIZES[WIDGET_TYPES.TEXT] || { w: 6, h: 2, minW: 2, minH: 1 };
      }

      // Calculate position — stack vertically
      const maxY = layout.reduce((max, item) => Math.max(max, item.y + item.h), 0);
      const newLayout = [
        ...layout,
        {
          i: widgetId,
          x: 0,
          y: maxY,
          w: size.w,
          h: size.h,
          minW: size.minW || 2,
          minH: size.minH || 1,
        },
      ];

      setLayout(newLayout);
      setWidgetConfigs((prev) => ({ ...prev, [widgetId]: widgetConfig }));

      logger.debug("[PortalPageEditor]", `Added widget ${widgetId} (${type})`);
    },
    [layout]
  );

  // Remove widget
  const handleRemoveWidget = useCallback(
    (widgetId) => {
      setLayout((prev) => prev.filter((item) => item.i !== widgetId));
      setWidgetConfigs((prev) => {
        const next = { ...prev };
        delete next[widgetId];
        return next;
      });
    },
    []
  );

  // Update widget config from dialog
  const handleUpdateWidgetConfig = useCallback((widgetId, config) => {
    setWidgetConfigs((prev) => ({ ...prev, [widgetId]: config }));
  }, []);

  // Layout change handler
  const handleLayoutChange = useCallback((newLayout) => {
    setLayout(newLayout);
  }, []);

  // Handle slug generation
  const handleSlugify = useCallback((name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
  }, []);

  // Save page
  const handleSave = useCallback(async () => {
    if (!pageMeta.name.trim()) {
      showSnackbar("error", "Validation", "Page name is required");
      return;
    }

    setSaving(true);
    try {
      const pageData = {
        id: isNew ? undefined : pageId,
        name: pageMeta.name,
        slug: pageMeta.slug || handleSlugify(pageMeta.name),
        description: pageMeta.description,
        isActive: pageMeta.isActive,
        layout,
        widgets: widgetConfigs,
      };

      const savedPage = await upsertPage(portalId, pageData);
      showSnackbar("success", "Success", t("portals.pageSaved") || "Page saved successfully");

      if (isNew && savedPage?.id) {
        navigate(`/admin/portals/${portalId}/pages/${savedPage.id}`, { replace: true });
      }
    } catch (err) {
      logger.error("[PortalPageEditor]", "Save error:", err.message);
      showSnackbar("error", "Error", err.message || "Failed to save page");
    } finally {
      setSaving(false);
    }
  }, [pageMeta, layout, widgetConfigs, isNew, pageId, portalId, upsertPage, handleSlugify, navigate, showSnackbar, t]);

  // Get display info for a widget
  const getWidgetLabel = useCallback(
    (widgetId) => {
      const config = widgetConfigs[widgetId];
      if (!config) return widgetId;
      if (config.type === "text") return "Text";
      if (config.registryId) {
        const registryWidgets = getAllWidgets();
        const found = registryWidgets.find((w) => w.id === config.registryId);
        return found?.title || config.registryId;
      }
      return widgetId;
    },
    [widgetConfigs]
  );

  if (initialLoading) {
    return (
      <KDBox display="flex" alignItems="center" justifyContent="center" minHeight="60vh">
        <CircularProgress size={48} />
      </KDBox>
    );
  }

  return (
    <KDBox p={3}>
      {/* Header */}
      <KDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <KDBox display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate(`/admin/portals/${portalId}`)} sx={{ color: textSecondary }}>
            <BackIcon />
          </IconButton>
          <KDBox>
            <KDTypography variant="h4" sx={{ color: textPrimary }}>
              {isNew ? (t("portals.newPage") || "New Page") : pageMeta.name}
            </KDTypography>
            {portal && (
              <KDTypography variant="body2" sx={{ color: textSecondary }}>
                Portal: {portal.name}
              </KDTypography>
            )}
          </KDBox>
        </KDBox>

        <KDBox display="flex" gap={1}>
          <KDButton
            variant="outlined"
            color={previewMode ? "success" : "secondary"}
            startIcon={<PreviewIcon />}
            onClick={() => setPreviewMode(!previewMode)}
          >
            {previewMode ? "Editing" : "Preview"}
          </KDButton>
          <KDButton
            variant="outlined"
            color="info"
            startIcon={<PaletteIcon />}
            onClick={() => setPaletteOpen(true)}
          >
            {t("portals.addWidget") || "Add Widget"}
          </KDButton>
          <KDButton
            variant="gradient"
            color="info"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : t("common.save") || "Save"}
          </KDButton>
        </KDBox>
      </KDBox>

      {/* Page metadata card */}
      {!previewMode && (
        <Card sx={{ p: 2.5, mb: 3, background: cardBg, border: `1px solid ${borderColor}`, borderRadius: "12px" }}>
          <KDBox display="flex" gap={2} flexWrap="wrap">
            <TextField
              label="Page Name"
              value={pageMeta.name}
              onChange={(e) => {
                setPageMeta((prev) => ({
                  ...prev,
                  name: e.target.value,
                  slug: isNew ? handleSlugify(e.target.value) : prev.slug,
                }));
              }}
              size="small"
              sx={{ ...inputSx, flex: 1, minWidth: 200 }}
            />
            <TextField
              label="Slug"
              value={pageMeta.slug}
              onChange={(e) => setPageMeta((prev) => ({ ...prev, slug: e.target.value }))}
              size="small"
              sx={{ ...inputSx, flex: 1, minWidth: 150 }}
            />
            <TextField
              label="Description"
              value={pageMeta.description}
              onChange={(e) => setPageMeta((prev) => ({ ...prev, description: e.target.value }))}
              size="small"
              sx={{ ...inputSx, flex: 2, minWidth: 250 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={pageMeta.isActive}
                  onChange={(e) => setPageMeta((prev) => ({ ...prev, isActive: e.target.checked }))}
                  color="success"
                  size="small"
                />
              }
              label={<KDTypography variant="body2" sx={{ color: textPrimary }}>Active</KDTypography>}
            />
          </KDBox>
        </Card>
      )}

      {/* Widget Grid */}
      {layout.length === 0 ? (
        <Card
          sx={{
            p: 6,
            textAlign: "center",
            background: cardBg,
            border: `2px dashed ${borderColor}`,
            borderRadius: "16px",
          }}
        >
          <PaletteIcon sx={{ fontSize: 48, color: textSecondary, mb: 1 }} />
          <KDTypography variant="h6" sx={{ color: textSecondary }}>
            {t("portals.emptyPage") || "No widgets yet"}
          </KDTypography>
          <KDTypography variant="body2" sx={{ color: palette.text.disabled, mt: 0.5 }}>
            {t("portals.emptyPageHint") || "Click \"Add Widget\" to start building this page"}
          </KDTypography>
          <KDButton
            variant="outlined"
            color="info"
            startIcon={<AddIcon />}
            onClick={() => setPaletteOpen(true)}
            sx={{ mt: 2 }}
          >
            {t("portals.addWidget") || "Add Widget"}
          </KDButton>
        </Card>
      ) : (
        <Box
          sx={{
            "& .react-grid-layout": {
              position: "relative",
            },
            "& .react-grid-item": {
              transition: "all 200ms ease",
            },
            "& .react-grid-placeholder": {
              background: `${palette.info.main}33`,
              borderRadius: "16px",
              border: `2px dashed ${palette.info.main}`,
            },
          }}
        >
          <ResponsiveGridLayout
            className="portal-page-editor-layout"
            layouts={{ lg: layout }}
            breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480 }}
            cols={{ lg: 12, md: 10, sm: 6, xs: 4 }}
            rowHeight={60}
            isDraggable={!previewMode}
            isResizable={!previewMode}
            containerPadding={[0, 0]}
            margin={[16, 16]}
            onLayoutChange={handleLayoutChange}
            draggableHandle=".widget-drag-handle"
          >
            {layout.map((item) => {
              const config = widgetConfigs[item.i];
              const label = getWidgetLabel(item.i);
              const isLocked = config?.userEditable === false || !config?.userEditable;

              return (
                <div key={item.i} data-grid={item}>
                  <Card
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      background: palette.background.glass,
                      border: previewMode
                        ? `1px solid ${borderColor}`
                        : `1px solid ${palette.info.main}4D`,
                      borderRadius: "16px",
                      overflow: "hidden",
                      transition: "border-color 0.2s ease",
                      position: "relative",
                      "&:hover .widget-toolbar": {
                        opacity: previewMode ? 0 : 1,
                      },
                    }}
                  >
                    {/* Edit toolbar (hidden in preview) */}
                    {!previewMode && (
                      <Box
                        className="widget-toolbar"
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          zIndex: 10,
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          px: 1,
                          py: 0.5,
                          opacity: 0,
                          transition: "opacity 0.2s ease",
                          background: darkMode
                            ? "linear-gradient(180deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0) 100%)"
                            : "linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0) 100%)",
                        }}
                      >
                        <IconButton
                          className="widget-drag-handle"
                          size="small"
                          sx={{ cursor: "grab", color: textSecondary }}
                        >
                          <DragIcon fontSize="small" />
                        </IconButton>

                        <KDTypography
                          variant="caption"
                          sx={{
                            color: textSecondary,
                            flex: 1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            fontWeight: 600,
                          }}
                        >
                          {label}
                        </KDTypography>

                        {isLocked ? (
                          <LockIcon sx={{ fontSize: 14, color: textSecondary, mr: 0.5 }} />
                        ) : (
                          <LockOpenIcon sx={{ fontSize: 14, color: palette.warning.main, mr: 0.5 }} />
                        )}

                        <Tooltip title="Configure">
                          <IconButton
                            size="small"
                            onClick={() => setConfigDialog({ open: true, widgetKey: item.i })}
                            sx={{ color: textSecondary }}
                          >
                            <ConfigIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveWidget(item.i)}
                            sx={{ color: palette.error.main }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}

                    {/* Widget content */}
                    <Box sx={{ flex: 1, overflow: "hidden" }}>
                      <WidgetRenderer
                        widgetId={item.i}
                        portalWidgetConfig={config}
                        language={portal?.defaultLanguage || "en"}
                        userEditable={false}
                      />
                    </Box>
                  </Card>
                </div>
              );
            })}
          </ResponsiveGridLayout>
        </Box>
      )}

      {/* Widget summary bar */}
      {layout.length > 0 && !previewMode && (
        <Card
          sx={{
            mt: 2,
            p: 1.5,
            background: cardBg,
            border: `1px solid ${borderColor}`,
            borderRadius: "8px",
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <KDTypography variant="caption" sx={{ color: textSecondary, fontWeight: 600 }}>
            {layout.length} widget{layout.length !== 1 ? "s" : ""}:
          </KDTypography>
          {layout.map((item) => {
            const config = widgetConfigs[item.i];
            return (
              <Chip
                key={item.i}
                label={getWidgetLabel(item.i)}
                size="small"
                onDelete={() => handleRemoveWidget(item.i)}
                icon={
                  config?.userEditable
                    ? <LockOpenIcon sx={{ fontSize: 14 }} />
                    : undefined
                }
                sx={{
                  color: textSecondary,
                  borderColor,
                  fontSize: "0.7rem",
                  height: 24,
                }}
                variant="outlined"
              />
            );
          })}
        </Card>
      )}

      {/* Widget Palette Drawer */}
      <WidgetPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        onAddWidget={handleAddWidget}
        darkMode={darkMode}
      />

      {/* Widget Config Dialog */}
      <WidgetConfigDialog
        open={configDialog.open}
        onClose={() => setConfigDialog({ open: false, widgetKey: null })}
        widgetKey={configDialog.widgetKey}
        widgetConfig={configDialog.widgetKey ? widgetConfigs[configDialog.widgetKey] : null}
        onSave={handleUpdateWidgetConfig}
        portalLanguages={portal?.supportedLanguages || ["en"]}
        darkMode={darkMode}
      />

      {/* Snackbar */}
      <KDSnackbar
        open={snackbar.open}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        severity={snackbar.severity}
        title={snackbar.title}
        message={snackbar.message}
      />
    </KDBox>
  );
}

export default PortalPageEditor;
